import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import AdminWorkspaces from "./pages/AdminWorkspaces";
import ApproveWorkspace from "./pages/ApproveWorkspace";
import Billing from "./pages/Billing";
import { BillingSuccess, BillingCancel } from "./pages/BillingResult";
import Dashboard from "./pages/Dashboard";
import Drivers from "./pages/Drivers";
import Trips from "./pages/Trips";
import PayCalculator from "./pages/PayCalculator";
import PayStatements from "./pages/PayStatements";
import PayStatementDetail from "./pages/PayStatementDetail";
import PayRun from "./pages/PayRun";
import Reports from "./pages/Reports";
import PayrollExport from "./pages/PayrollExport";
import AuditTrail from "./pages/AuditTrail";
import TeamAccess from "./pages/TeamAccess";
import DeductionsLibrary from "./pages/DeductionsLibrary";
import Expenses from "./pages/Expenses";
import DriverPortal from "./pages/DriverPortal";
import Settings from "./pages/Settings";
import AcceptInvite from "./pages/AcceptInvite";

function Protected({ children, superadmin = false, allowPaywalled = false }) {
  const { user, loading, billing } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/home" replace />;
  if (superadmin && user.role !== "superadmin") return <Navigate to="/" replace />;
  if (!superadmin && user.role === "superadmin") return <Navigate to="/admin/workspaces" replace />;
  // Trial expired + no active sub -> force to /billing. Super-admins and the
  // billing routes themselves are always reachable so the user can pay.
  if (!superadmin && !allowPaywalled && billing?.paywall) {
    return <Navigate to="/billing" replace />;
  }
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/home" element={<Landing />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/approve" element={<ApproveWorkspace />} />
          <Route path="/driver" element={<DriverPortal />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route
            path="/billing"
            element={
              <Protected allowPaywalled>
                <Billing />
              </Protected>
            }
          />
          <Route
            path="/billing/success"
            element={
              <Protected allowPaywalled>
                <BillingSuccess />
              </Protected>
            }
          />
          <Route
            path="/billing/cancel"
            element={
              <Protected allowPaywalled>
                <BillingCancel />
              </Protected>
            }
          />
          <Route
            path="/admin/workspaces"
            element={
              <Protected superadmin>
                <AdminWorkspaces />
              </Protected>
            }
          />
          <Route
            path="/"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="drivers" element={<Drivers />} />
            <Route path="trips" element={<Trips />} />
            <Route path="pay-calculator" element={<PayCalculator />} />
            <Route path="pay-statements" element={<PayStatements />} />
            <Route path="pay-statements/:id" element={<PayStatementDetail />} />
            <Route path="pay-run" element={<PayRun />} />
            <Route path="reports" element={<Reports />} />
            <Route path="payroll-export" element={<PayrollExport />} />
            <Route path="audit-trail" element={<AuditTrail />} />
            <Route path="team" element={<TeamAccess />} />
            <Route path="deductions" element={<DeductionsLibrary />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="settings" element={<Settings />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
